import { createFileRoute } from "@tanstack/react-router";
import { Flame } from "lucide-react";
import { MarkerIcon, ClockIcon, MusicIcon } from "@/components/icons/LocationIcons";
import { useEntry, useEntries, useField } from "@/lib/content";

const SITE_URL = "https://tacoya.snapinsolutions.com";

const RESTAURANTS = [
  {
    name: "Tacoya Kamppi Flagship",
    street: "Lapinlahdenkatu",
    postal: "00180",
    hours: ["Mo-Sa 11:00-22:00", "Su 12:00-21:00"],
    status: "OPEN",
  },
  {
    name: "Tacoya Vaasankatu",
    street: "Vaasankatu",
    postal: "00500",
    hours: [],
    status: "SOON",
  },
];

export const Route = createFileRoute("/locations")({
  head: () => ({
    meta: [
      { title: "Locations — Tacoya in Helsinki" },
      { name: "description", content: "Find Tacoya in Helsinki. Kamppi flagship birrieria on Lapinlahdenkatu, with more locations coming." },
      { property: "og:title", content: "Tacoya Locations" },
      { property: "og:description", content: "Visit Tacoya in Helsinki." },
      { property: "og:url", content: `${SITE_URL}/locations` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/locations` }],
    scripts: [
      ...RESTAURANTS.filter((r) => r.status === "OPEN").map((r) => ({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Restaurant",
          name: r.name,
          url: `${SITE_URL}/locations`,
          servesCuisine: ["Mexican", "Street Food"],
          priceRange: "€€",
          address: {
            "@type": "PostalAddress",
            streetAddress: r.street,
            postalCode: r.postal,
            addressLocality: "Helsinki",
            addressCountry: "FI",
          },
          openingHours: r.hours,
        }),
      })),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Locations", item: `${SITE_URL}/locations` },
          ],
        }),
      },
    ],
  }),
  component: LocationsPage,
});

function LocationsPage() {
  const f = useField();
  const hero = useEntry("locations", "hero");
  const locations = useEntries("locations", "items");
  const next = useEntry("locations", "next_stops");

  return (
    <div className="relative overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute inset-0 z-0">
        <div className="fire-glow absolute inset-x-0 bottom-0 h-[55vh]" />
        <div className="absolute -top-32 -left-32 size-[520px] rounded-full bg-chili/20 blur-3xl animate-pulse" style={{ animationDuration: "5s" }} />
        <div className="absolute top-1/3 -right-40 size-[600px] rounded-full bg-yellow/10 blur-3xl animate-pulse" style={{ animationDuration: "7s" }} />
        <div className="absolute bottom-0 left-1/3 size-[420px] rounded-full bg-chili/15 blur-3xl" />

        <div className="smoke-plume" style={{ left: "12%", animationDuration: "11s", animationDelay: "0s", ["--dx-smoke" as never]: "40px" }} />
        <div className="smoke-plume" style={{ left: "38%", animationDuration: "14s", animationDelay: "-3s", ["--dx-smoke" as never]: "-30px", ["--scale" as never]: "1.3" }} />
        <div className="smoke-plume" style={{ left: "68%", animationDuration: "13s", animationDelay: "-6s", ["--dx-smoke" as never]: "50px" }} />
        <div className="smoke-plume" style={{ left: "88%", animationDuration: "16s", animationDelay: "-9s", ["--dx-smoke" as never]: "-40px", ["--scale" as never]: "1.4" }} />

        {Array.from({ length: 18 }).map((_, i) => (
          <span
            key={i}
            className="ember"
            style={{
              left: `${(i * 5.7 + 4) % 100}%`,
              animationDuration: `${5 + (i % 5) * 1.4}s`,
              animationDelay: `${-(i * 0.6) % 8}s`,
              ["--dx" as never]: `${(i % 2 === 0 ? 1 : -1) * (8 + (i % 4) * 6)}px`,
            }}
          />
        ))}

        <Flame className="absolute top-[18%] left-[6%] size-7 text-chili/40 animate-pulse" style={{ animationDuration: "3s" }} />
        <Flame className="absolute top-[42%] right-[8%] size-9 text-yellow/30 rotate-12" style={{ animation: "float-slow 6s ease-in-out infinite" }} />
        <Flame className="absolute top-[68%] left-[14%] size-6 text-chili/35 -rotate-6" style={{ animation: "float-slow 7s ease-in-out infinite" }} />
        <span className="absolute top-[28%] right-[18%] text-3xl opacity-30 rotate-12" style={{ animation: "float-slow 8s ease-in-out infinite" }}>🌶️</span>
        <span className="absolute top-[78%] right-[12%] text-2xl opacity-25 -rotate-12" style={{ animation: "float-slow 9s ease-in-out infinite" }}>🌶️</span>
        <span className="absolute top-[55%] left-[42%] text-xl opacity-20" style={{ animation: "float-slow 10s ease-in-out infinite" }}>🔥</span>

        <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: "linear-gradient(var(--cream) 1px, transparent 1px), linear-gradient(90deg, var(--cream) 1px, transparent 1px)", backgroundSize: "80px 80px" }} />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-5 md:px-8 pt-10 md:pt-14 pb-10 md:pb-14">
        <header className="mb-10">
          <div className="inline-flex items-center gap-2 mb-4">
            <Flame size={14} className="text-chili fill-chili animate-pulse" />
            <span className="text-xs uppercase tracking-[0.3em] text-chili font-bold">{f(hero, "kicker", "Find Us")}</span>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl md:text-8xl leading-[0.95] tracking-tight text-cream">
            {f(hero, "heading_a", "WHERE")} <span className="text-yellow">{f(hero, "heading_b", "TO EAT.")}</span>
          </h1>
        </header>

        <div className="grid md:grid-cols-2 gap-6">
          {locations.map((l) => {
            const name = f(l, "name");
            const address = f(l, "address");
            const status = String(l.data.status ?? "OPEN");
            return (
              <article key={l.key} className="group relative overflow-hidden rounded-2xl border border-border bg-card hover:border-yellow/70 transition-colors shadow-[0_10px_40px_-20px_rgba(0,0,0,0.8)]">
                <div className="relative aspect-[4/3] overflow-hidden">
                  {l.image && <img src={l.image} alt={name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/50 to-transparent" />
                  <span className={`absolute top-4 right-4 text-[10px] uppercase tracking-widest px-3 py-1 font-bold rounded-full ${status === "OPEN" ? "bg-yellow text-charcoal" : "bg-chili text-cream"}`}>
                    {status}
                  </span>
                  <div className="absolute bottom-5 left-5 right-5">
                    <div className="text-[10px] uppercase tracking-widest text-yellow">{f(l, "tag")}</div>
                    <h2 className="font-display text-4xl md:text-5xl text-cream leading-none mt-1">{name}</h2>
                  </div>
                </div>
                <div className="p-6 md:p-7">
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3 text-sm text-cream/90">
                      <span className="flex items-center justify-center w-9 h-9 rounded-full bg-chili/15 shrink-0">
                        <MarkerIcon size={16} className="text-chili" />
                      </span>
                      <span>{address}</span>
                    </li>
                    <li className="flex items-center gap-3 text-sm text-cream/90">
                      <span className="flex items-center justify-center w-9 h-9 rounded-full bg-chili/15 shrink-0">
                        <ClockIcon size={16} className="text-chili" />
                      </span>
                      <span>{f(l, "hours")}</span>
                    </li>
                    <li className="flex items-center gap-3 text-sm text-cream/90">
                      <span className="flex items-center justify-center w-9 h-9 rounded-full bg-chili/15 shrink-0">
                        <MusicIcon size={16} className="text-chili" />
                      </span>
                      <span>{f(l, "vibe")}</span>
                    </li>
                  </ul>
                  <div className="mt-6 pt-5 border-t border-border/60 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    {status === "OPEN" ? (
                      <>
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-yellow text-charcoal px-5 py-3 sm:py-2.5 text-[11px] sm:text-xs uppercase tracking-widest font-bold rounded-full hover:bg-cream transition-colors shadow-[0_10px_25px_-10px_color-mix(in_oklab,var(--tacoya-yellow)_70%,transparent)]"
                        >
                          <MarkerIcon size={14} /> Open in Maps →
                        </a>
                        <span className="text-[10px] uppercase tracking-[0.25em] text-cream/50 text-center sm:text-right">Walk-in welcome</span>
                      </>
                    ) : (
                      <>
                        <span className="inline-flex w-full sm:w-auto items-center justify-center gap-2 border border-cream/20 text-cream/80 px-5 py-3 sm:py-2.5 text-[11px] sm:text-xs uppercase tracking-widest font-bold rounded-full">
                          Notify Me
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.25em] text-yellow text-center sm:text-right">Opening soon</span>
                      </>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <div
          className="mt-20 relative overflow-hidden rounded-3xl border border-cream/10 bg-gradient-to-br from-chili/15 via-charcoal/60 to-charcoal p-8 md:p-14 text-center shadow-[0_40px_80px_-40px_rgba(0,0,0,0.9),inset_0_1px_0_0_rgba(255,255,255,0.08)]"
          style={{ fontFamily: "'Poppins', sans-serif" }}
        >
          <span aria-hidden className="pointer-events-none absolute -top-24 -left-24 size-72 rounded-full bg-chili/25 blur-3xl" />
          <span aria-hidden className="pointer-events-none absolute -bottom-32 -right-24 size-80 rounded-full bg-yellow/15 blur-3xl" />
          <span aria-hidden className="pointer-events-none absolute inset-x-16 top-0 h-px bg-gradient-to-r from-transparent via-cream/40 to-transparent" />

          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-chili/40 bg-chili/15 mb-6">
              <span className="size-1.5 rounded-full bg-chili animate-pulse" />
              <span className="text-[10px] uppercase tracking-[0.18em] text-chili font-bold">{f(next, "kicker", "Next Stops")}</span>
            </div>

            <h2 className="text-3xl md:text-5xl lg:text-6xl text-cream font-extrabold leading-tight">
              {f(next, "cities", "Tampere · Turku · Stockholm · Oslo")}
            </h2>

            <p className="mt-5 text-cream/70 max-w-xl mx-auto leading-relaxed">
              {f(next, "blurb")}
            </p>

            <a
              href={String(next?.data.cta_href ?? "/franchise")}
              className="group mt-8 inline-flex items-center gap-3 bg-yellow text-charcoal pl-6 pr-2 py-2 text-sm uppercase tracking-[0.15em] font-bold rounded-full hover:bg-cream transition-colors shadow-[0_18px_40px_-12px_color-mix(in_oklab,var(--yellow)_60%,transparent)]"
            >
              <span>{f(next, "cta_label", "Bring Tacoya here")}</span>
              <span className="flex items-center justify-center size-9 rounded-full bg-charcoal text-yellow group-hover:bg-chili group-hover:text-cream transition-colors">
                →
              </span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
